import { useEffect } from 'react';
import { useHairSession } from '../context/HairSessionContext';
import {
  HAIR_PAGE_SETUP_TOKEN_KEY as Hair_PAGE_SETUP_TOKEN_KEY,
  HAIR_PAGE_SETUP_DATA_PREFIX as Hair_PAGE_SETUP_DATA_PREFIX,
} from '../config/hairApp';
import { SESSION_APP_DATA_BASE as HAIR_SESSION_APP_DATA_BASE } from '../config/sessionAppData';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import {
  SESSION_APP_DATA_BASE as SALON_SESSION_APP_DATA_BASE,
  SESSION_MASTER_TOKEN_URL,
} from '../../../../enterprise/salon/config/sessionAppData';
import {
  SALON_PAGE_SETUP_TOKEN_KEY,
  SALON_PAGE_SETUP_DATA_PREFIX,
} from '../../../../enterprise/salon/config/salonApp';
import { getSalonAppSessionToken } from '../../../../enterprise/salon/utils/salonAppSessionToken';
import {
  franchiseAuthHeaders,
  getFranchiseId,
  getFranchiseOrganizationId,
  hasFranchiseFlowAuth,
} from '../../../../enterprise/shared/franchiseFlowAuth';

function hexToRgb(hex) {
  if (!hex || typeof hex !== 'string') return '161, 224, 244';
  const n = parseInt(hex.replace(/^#/, ''), 16);
  if (Number.isNaN(n)) return '161, 224, 244';
  return [n >> 16, (n >> 8) & 255, n & 255].join(', ');
}

function themeRoots() {
  const roots = [document.documentElement];
  try {
    document.querySelectorAll('.enterprise-salon-lux').forEach((el) => {
      if (el) roots.push(el);
    });
  } catch (_) {
    /* ignore */
  }
  return roots;
}

function setThemeProperty(name, value) {
  if (!name || value == null || value === '') return;
  themeRoots().forEach((el) => {
    try {
      el.style.setProperty(name, value);
    } catch (_) {
      /* ignore */
    }
  });
}

function normalizeGradientAngle(angle, fallback = 90) {
  const parsedAngle = Number(angle);
  if (!Number.isFinite(parsedAngle)) return fallback;
  return ((parsedAngle % 360) + 360) % 360;
}

function buildBackgroundGradient(primaryColor, secondaryColor, angle) {
  const normalizedAngle = normalizeGradientAngle(angle);
  const isVerticalHalfBlend = Math.abs(normalizedAngle) < 0.01 || Math.abs(normalizedAngle - 180) < 0.01;

  if (isVerticalHalfBlend) {
    return `linear-gradient(${normalizedAngle}deg, ${primaryColor} 0%, ${primaryColor} 30%, ${secondaryColor} 70%, ${secondaryColor} 100%)`;
  }

  return `linear-gradient(${normalizedAngle}deg, ${primaryColor} 0%, ${secondaryColor} 100%)`;
}

function applyHighlightFromExternalFields(highlight) {
  const color =
    typeof highlight === 'string'
      ? highlight
      : highlight && typeof highlight === 'object'
        ? highlight.primary || highlight.secondary
        : null;
  if (!color || typeof color !== 'string') return;
  setThemeProperty('--skin-highlight', color);
  setThemeProperty('--skin-highlight-rgb', hexToRgb(color));
}

function applyFontsFromExternalFields(fonts) {
  if (!fonts || typeof fonts !== 'object') return;
  if (fonts.primary) {
    setThemeProperty('--skin-text-primary', fonts.primary);
  }
  if (fonts.secondary) {
    setThemeProperty('--skin-text-secondary', fonts.secondary);
  }
  if (fonts.muted) {
    setThemeProperty('--skin-text-muted', fonts.muted);
  }
}

function applyCtaFromExternalFields(cta) {
  if (!cta || typeof cta !== 'object') return;
  const primary = cta.primary_color;
  const secondary = cta.secondary_color;
  const backgroundType = (cta.background_type || 'solid').toLowerCase();
  const angle = typeof cta.gradient_angle === 'number' ? cta.gradient_angle : 90;
  const textColor = cta.text_color;
  const borderColor = cta.border_color;

  if (primary) {
    setThemeProperty('--cta-primary', primary);
    setThemeProperty('--cta-primary-rgb', hexToRgb(primary));
  }
  if (secondary) {
    setThemeProperty('--cta-secondary', secondary);
    setThemeProperty('--cta-secondary-rgb', hexToRgb(secondary));
  }
  if (textColor) {
    setThemeProperty('--cta-text-color', textColor);
  }
  if (borderColor) {
    setThemeProperty('--cta-border-color', borderColor);
  }
  if (backgroundType === 'gradient' && primary && secondary) {
    setThemeProperty(
      '--cta-background',
      `linear-gradient(${angle}deg, ${primary} 0%, ${secondary} 100%)`
    );
  } else if (primary || secondary) {
    setThemeProperty('--cta-background', primary || secondary);
  }
}

function applyBordersFromExternalFields(borders) {
  if (!borders) return;
  const color =
    typeof borders === 'string'
      ? borders
      : borders && typeof borders === 'object'
        ? borders.color || borders.primary
        : null;
  if (!color || typeof color !== 'string') return;
  setThemeProperty('--skin-border-color', color);
  setThemeProperty('--skin-border-color-rgb', hexToRgb(color));
}

export function applyPageBgFromExternalFields(externalFields) {
  const bg = externalFields?.background;
  const primaryColor = bg?.primary_color ?? externalFields?.primary_color;
  const secondaryColor = bg?.secondary_color ?? externalFields?.secondary_color;
  const type = (bg?.type || '').toLowerCase();
  const gradientAngle = normalizeGradientAngle(bg?.gradient_angle, 90);
  const hasAnyColor = primaryColor || secondaryColor;
  if (!hasAnyColor) return;

  const isDefaultSkinPink =
    primaryColor?.toLowerCase() === '#db2777' || secondaryColor?.toLowerCase() === '#db2777';
  if (isDefaultSkinPink) {
    themeRoots().forEach((el) => {
      try {
        el.style.removeProperty('--page-bg-background');
        el.style.removeProperty('--page-bg-primary');
        el.style.removeProperty('--page-bg-secondary');
      } catch (_) {
        /* ignore */
      }
    });
    return;
  }

  const useGradient = type === 'gradient' && primaryColor && secondaryColor;
  if (useGradient) {
    setThemeProperty(
      '--page-bg-background',
      buildBackgroundGradient(primaryColor, secondaryColor, gradientAngle)
    );
    setThemeProperty('--page-bg-primary', primaryColor);
    setThemeProperty('--page-bg-secondary', secondaryColor);
    setThemeProperty('--page-bg-primary-rgb', hexToRgb(primaryColor));
    setThemeProperty('--page-bg-secondary-rgb', hexToRgb(secondaryColor));
  } else {
    const solidColor = primaryColor || secondaryColor;
    setThemeProperty('--page-bg-background', solidColor);
    setThemeProperty('--page-bg-primary', solidColor);
    setThemeProperty('--page-bg-secondary', solidColor);
    setThemeProperty('--page-bg-primary-rgb', hexToRgb(solidColor));
    setThemeProperty('--page-bg-secondary-rgb', hexToRgb(solidColor));
  }
}

export const PAGE_SETUP_STORAGE_KEY = Hair_PAGE_SETUP_TOKEN_KEY;
export const PAGE_SETUP_DATA_PREFIX = Hair_PAGE_SETUP_DATA_PREFIX;

export function getPageSetupDataKey(token) {
  return token ? PAGE_SETUP_DATA_PREFIX + String(token).trim() : null;
}

export function storePageSetupData(token, result) {
  const key = getPageSetupDataKey(token);
  if (!key || !result) return;
  try {
    sessionStorage.setItem(key, JSON.stringify(result));
    sessionStorage.setItem(PAGE_SETUP_STORAGE_KEY, token);
  } catch (_) {}
}

export function applyPageSetupResult(result) {
  if (!result) return;
  const externalFields =
    result?.external_fields ||
    result?.data?.external_fields ||
    (Array.isArray(result) && result[0]?.external_fields);
  if (externalFields) {
    applyPageBgFromExternalFields(externalFields);
    applyCtaFromExternalFields(externalFields?.cta);
    applyFontsFromExternalFields(externalFields?.fonts);
    applyBordersFromExternalFields(externalFields?.borders);
    applyHighlightFromExternalFields(externalFields?.highlight);
  }
  const domain = result?.domain ?? result?.data?.domain;
  if (domain && typeof domain === 'string') {
    document.documentElement.dataset.pageSetupDomain = domain;
  }
}

function applyStoredByPrefix(tokenKey, dataPrefix) {
  try {
    const token = sessionStorage.getItem(tokenKey);
    if (!token) return false;
    const raw = sessionStorage.getItem(dataPrefix + String(token).trim());
    if (!raw) return false;
    applyPageSetupResult(JSON.parse(raw));
    return true;
  } catch (_) {
    return false;
  }
}

function pageSetupQuery() {
  const qs = new URLSearchParams({ _: String(Date.now()) });
  const orgId = getFranchiseOrganizationId();
  if (orgId) qs.set('organization_id', orgId);
  const franchiseId = getFranchiseId();
  if (franchiseId) qs.set('franchise_id', franchiseId);
  qs.set('flow', 'hair');
  return qs.toString();
}

async function resolveSalonToken() {
  const live = getSalonAppSessionToken();
  if (live) return live;
  try {
    const stored = sessionStorage.getItem(SALON_PAGE_SETUP_TOKEN_KEY);
    if (stored) return stored;
  } catch (_) {}
  try {
    const res = await fetchWithTimeout(`${SESSION_MASTER_TOKEN_URL}?_=${Date.now()}`, {
      timeoutMs: 12000,
      cache: 'no-store',
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) return data?.public_token || '';
  } catch (_) {}
  return '';
}

/**
 * Fetch org page-setup (workspace theme) and apply background, CTA, fonts, borders.
 * Reuses salon kiosk cache when Hair Analyze is opened from the enterprise hub.
 */
export function PageBackgroundSync() {
  const { token: hairToken } = useHairSession();

  useEffect(() => {
    applyStoredByPrefix(SALON_PAGE_SETUP_TOKEN_KEY, SALON_PAGE_SETUP_DATA_PREFIX);
    applyStoredByPrefix(Hair_PAGE_SETUP_TOKEN_KEY, Hair_PAGE_SETUP_DATA_PREFIX);
    if (hairToken) {
      try {
        const stored = sessionStorage.getItem(getPageSetupDataKey(hairToken));
        if (stored) applyPageSetupResult(JSON.parse(stored));
      } catch (_) {}
    }

    let cancelled = false;

    (async () => {
      const orgId = getFranchiseOrganizationId();
      const salonToken = orgId ? await resolveSalonToken() : getSalonAppSessionToken();
      const token = String(hairToken || salonToken || '').trim();
      if (!token && !orgId) return;

      const fetchToken = token || (await resolveSalonToken());
      if (!fetchToken || cancelled) return;

      const salonUrl = `${SALON_SESSION_APP_DATA_BASE}/${encodeURIComponent(fetchToken)}/page-setup?${pageSetupQuery()}`;
      const hairUrl = `${HAIR_SESSION_APP_DATA_BASE}/${encodeURIComponent(fetchToken)}/page-setup?${pageSetupQuery()}`;
      const useSalonSessionApi =
        Boolean(orgId) ||
        Boolean(salonToken) ||
        Boolean(getFranchiseOrganizationId()) ||
        hasFranchiseFlowAuth();
      const url = useSalonSessionApi ? salonUrl : hairUrl;

      try {
        const res = await fetchWithTimeout(url, {
          timeoutMs: 20000,
          cache: 'no-store',
          credentials: 'include',
          headers: franchiseAuthHeaders(),
        });
        const result = res.ok ? await res.json() : null;
        if (cancelled || !result) return;
        applyPageSetupResult(result);
        storePageSetupData(fetchToken, result);
        try {
          sessionStorage.setItem(SALON_PAGE_SETUP_TOKEN_KEY, fetchToken);
          sessionStorage.setItem(SALON_PAGE_SETUP_DATA_PREFIX + fetchToken, JSON.stringify(result));
        } catch (_) {}
      } catch (_) {}
    })();

    return () => {
      cancelled = true;
    };
  }, [hairToken]);

  return null;
}
